import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { AnyEntity } from "@mapmycustomers/shared/types/entity";

import ViewMode from "@app/enum/dashboard/ViewMode";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { TabbedDrillDownListFetcherPayload } from "@app/scene/dashboard/types/TabbedDrillDownListFetcher";
import EntityTypesSupportedInSkippedRecords from "@app/types/dashboard/cards/skippedRecords/EntityTypesSupportedInSkippedRecords";
import SkippedRecordsCardConfiguration from "@app/types/dashboard/cards/skippedRecords/SkippedRecordsCardConfiguration";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";
import MapViewportState from "@app/types/map/MapViewportState";

import SkippedRecordsCardData from "./SkippedRecordsCardData";

export const fetchSkippedRecordsCardData = createAction("@dashboard/fetchSkippedRecordsCardData")<{
  callback: (data: SkippedRecordsCardData) => void;
  cardId: DashboardCard["id"];
  configuration: SkippedRecordsCardConfiguration;
  failureCallback?: () => void;
  offset?: number; // used with List view mode when we need to load more items
  scope?: Scope;
  spansBack?: number; // whole day/week spans stepped back from the configured one; 0 = configured
  viewMode: ViewMode;
  viewport?: MapViewportState; // used with Map view mode when user pans/zooms
}>();

export const showSkippedRecordsDrillDown = createAsyncAction(
  "@dashboard/showSkippedRecordsCardDrillDownModal/request",
  "@dashboard/showSkippedRecordsCardDrillDownModal/success",
  "@dashboard/showSkippedRecordsCardDrillDownModal/failure"
)<
  {
    configuration: SkippedRecordsCardConfiguration;
    scope: Scope;
    // The card face can be showing an earlier span than the saved configuration. View Data has
    // to match what the user is looking at, so the offset travels with the request.
    spansBack?: number;
  },
  { viewStates: Record<EntityTypesSupportedInSkippedRecords, DrillDownViewState> },
  void
>();

export const hideSkippedRecordsDrillDown = createAction(
  "@dashboard/hideSkippedRecordsCardDrillDownModal"
)<void>();

export const fetchSkippedRecordsCardDrillDownData = createAsyncAction(
  "@dashboard/fetchSkippedRecordsCardDrillDownData/request",
  "@dashboard/fetchSkippedRecordsCardDrillDownData/success",
  "@dashboard/fetchSkippedRecordsCardDrillDownData/failure"
)<
  TabbedDrillDownListFetcherPayload<AnyEntity, EntityTypesSupportedInSkippedRecords>,
  {
    entityType: EntityTypesSupportedInSkippedRecords;
    totalFilteredRecords: number;
    totalRecords: number;
  },
  unknown
>();

export const applySkippedRecordsDrillDownListViewSettings = createAction(
  "@dashboard/applySkippedRecordsDrillDownListViewSettings"
)<Partial<DrillDownViewState> & { entityType: EntityTypesSupportedInSkippedRecords }>();

export const exportSkippedRecordsCardDrillDownData = createAction(
  "@dashboard/exportSkippedRecordsCardData"
)<{
  configuration: SkippedRecordsCardConfiguration;
  entityType: EntityTypesSupportedInSkippedRecords;
  scope: Scope;
}>();

export type SkippedRecordsCardActions = ActionType<
  | typeof applySkippedRecordsDrillDownListViewSettings
  | typeof exportSkippedRecordsCardDrillDownData
  | typeof fetchSkippedRecordsCardData
  | typeof fetchSkippedRecordsCardDrillDownData
  | typeof hideSkippedRecordsDrillDown
  | typeof showSkippedRecordsDrillDown
>;
